import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

type TaskbarProps = {
  compact?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
};

export default function Taskbar({
  compact = false,
  collapsed = false,
  onToggleCollapse,
}: TaskbarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  function generateGuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  const go = (path: string) => navigate(path);

  const onLogoutClicked = () => {
    localStorage.clear();
    navigate("/");
  };

  const routeNeedsSidebar = location.pathname.startsWith("/supportbot") || location.pathname.startsWith("/mac") || location.pathname.startsWith("/amp/");
  const showCollapseToggle = routeNeedsSidebar && onToggleCollapse;

  return (
    <nav
      className={`amp-taskbar-component${compact ? " compact" : ""}`}
      aria-label="Quick actions"
    >
      {showCollapseToggle && (
        <div className="amp-taskbar-item-wrap top-toggle">
          <button
            className="amp-taskbar-item-component"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => onToggleCollapse && onToggleCollapse()}
            aria-pressed={collapsed}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              {collapsed ? (
                <path d="M8 5l8 7-8 7" />
              ) : (
                <path d="M16 5l-8 7 8 7" />
              )}
            </svg>
          </button>
        </div>
      )}

      <div className="amp-taskbar-item-wrap">
        <button
          className={`amp-taskbar-item-component ${
            location.pathname.startsWith("/supportbot") || location.pathname.startsWith("/amp/") ? "active" : ""
          }`}
          title="SupportBot"
          onClick={() => {
            const newGuid = generateGuid();
            navigate(`/supportbot/${newGuid}`);
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 10.5l9-7 9 7" />
            <path d="M5 9v11h5v-6h4v6h5V9" />
          </svg>
        </button>
        <span className="amp-taskbar-label">Home</span>
      </div>

      <div className="amp-taskbar-item-wrap">
        <button
          className={`amp-taskbar-item-component ${
            location.pathname.startsWith("/amps") ? "active" : ""
          }`}
          title="AMPs"
          onClick={() => go("/amps")}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
          </svg>
        </button>
        <span className="amp-taskbar-label">AMPs</span>
      </div>

      <div className="amp-taskbar-item-wrap">
        <button
          className={`amp-taskbar-item-component ${
            location.pathname.startsWith("/mac") ? "active" : ""
          }`}
          title="Mac"
          onClick={() => go("/mac")}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="4" width="18" height="14" rx="2" />
            <path d="M8 20h8" />
          </svg>
        </button>
        <span className="amp-taskbar-label">Mac</span>
      </div>

      <div className="amp-taskbar-item-wrap">
        <button
          className={`amp-taskbar-item-component ${
            location.pathname.startsWith("/billing") ? "active" : ""
          }`}
          title="Billing"
          onClick={() => go("/billing")}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="2" y="7" width="20" height="14" rx="2" />
            <path d="M2 11h20" />
          </svg>
        </button>
        <span className="amp-taskbar-label">Billing</span>
      </div>

      <div className="amp-taskbar-item-wrap">
        <button
          className={`amp-taskbar-item-component ${
            location.pathname.startsWith("/settings") ? "active" : ""
          }`}
          title="Settings"
          onClick={() => go("/settings")}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
        <span className="amp-taskbar-label">Settings</span>
      </div>
      <div style={{ flex: 1 }} />

      <div className="amp-taskbar-item-wrap">
        <button
          className="amp-taskbar-item-component"
          title="Logout"
          onClick={onLogoutClicked}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
            <line x1="12" y1="2" x2="12" y2="12" />
          </svg>
        </button>
        <span className="amp-taskbar-label">Logout</span>
      </div>
    </nav>
  );
}
